// Finding a pack item's place inside the research document (the pack's NotionEditor HTML).
//
// The doc carries no ids — the editor strips attributes and the user rewrites it freely — so an
// item is located the same way syncDocDeletions decides one is gone: photos and links by their
// URL, snippets by a solid slice of their text. A ten-page document scrolls to the match; when
// the exact item has been edited away, the item's SECTION heading is the next best landing.

import { canon } from '@/lib/packDocExtractions'
import { isPhotoSource } from '@/lib/photoFallback'

const norm = (t) => String(t || '').replace(/\s+/g, ' ').trim().toLowerCase()

// Section heading a kind lands under when the exact item can't be found — the headings
// composePackStartDoc writes. A kind may name SEVERAL: pictures are under "Photos" now (the
// frame's word), and every document written before that says "Images" — those documents are saved
// and are the user's, so the older heading stays a landing place rather than being migrated. The same
// goes for "Snippets": the section is written as "Extractions" since 2026-09-15 (Pouria), and every
// document written before that still says "Snippets". Lower-case, like the norm() they meet —
// "Extractions" never matched, so an extraction whose text wasn't found got no landing at all (#575).
// Videos have their own section and products sit in "Links" since the document started drawing
// them (Pouria, 2026-10-01 — lib/packDocUpdate.js `docRole`).
const SECTION = { photo: ['photos', 'images'], note: ['extractions', 'snippets'], receipt: ['extractions', 'snippets'], link: ['links'], video: ['videos'], product: ['links'], comparison: ['comparison'], conclusion: ['conclusion'] }

/**
 * The block in `root` that carries `text`, or null — WHERE in the document something was made
 * (frame 40007893:378129's "Task created here").
 *
 * Same identity rule as findDocTarget's snippet branch and syncDocDeletions: a solid slice of the
 * text, matched against the document's blocks. Deliberately WITHOUT the section-heading fallback —
 * an item that can no longer be found still belongs to its section, but a callout pointing at a
 * heading would be claiming the task was created somewhere it was not. No match, no callout.
 */
export function findTextAnchor(root, text) {
  const key = norm(text).slice(0, 60)
  if (!root || key.length < 12) return null
  for (const el of root.querySelectorAll('h3, h4, p, li, blockquote')) {
    if (norm(el.textContent).includes(key)) return el
  }
  return null
}

/**
 * The element in `root` that shows research item `item` ITSELF, or null — its own picture, its own
 * link, its own words. No section fallback: this answers "where is this item", not "roughly where
 * does it belong", which is what the "Task created here" callout needs (§67-4) and what
 * `findDocTarget` adds the heading to.
 *
 * @param root an Element containing the rendered document
 * @param item a research item: { kind, url, content, title }
 */
export function findItemAnchor(root, item) {
  if (!root || !item) return null
  const url = item.url || (item.kind === 'photo' ? item.content : '')

  // A photo is found by ANY of its candidates (busy-my-web#691): the document holds its full URL,
  // but a picture that failed to load there has had its src switched to the thumbnail on screen.
  if (item.kind === 'photo' && url) {
    for (const img of root.querySelectorAll('img')) {
      const src = img.getAttribute('src')
      if (src === url || isPhotoSource(item, src)) return img
    }
  }
  // A video and a product are link LINES in the document too (2026-10-01), found the same way.
  if (['link', 'video', 'product'].includes(item.kind) && url) {
    for (const a of root.querySelectorAll('a')) {
      if (a.getAttribute('href') === url) return a.closest('li') || a
    }
  }

  // Snippet (and anything else with text): the first block whose text carries a solid piece of it.
  // Compared whitespace-free (#575): a snippet's newlines are <br> in the doc, which has no text,
  // so "a\nb" read "ab" there and the extraction was never found.
  const key = canon(item.content || item.title).slice(0, 60)
  if (key.length >= 12) {
    for (const el of root.querySelectorAll('h4, p, li, blockquote')) {
      if (canon(el.textContent).includes(key)) return el
    }
  }

  return null
}

/**
 * The element in `root` (the editor's DOM) that shows research item `item`, or null — the item
 * itself, or failing that the SECTION it belongs to, which is a good enough answer for "scroll me
 * to this".
 * @param root an Element containing the rendered document
 * @param item a research item: { kind, url, content, title }
 */
export function findDocTarget(root, item) {
  const own = findItemAnchor(root, item)
  if (own) return own
  if (!root || !item) return null

  // Fall back to the item's section heading.
  const section = SECTION[item.kind]
  if (section) {
    for (const h of root.querySelectorAll('h3')) {
      if (section.includes(norm(h.textContent))) return h
    }
  }
  return null
}
